/**
 * Coups de pouce — Attendu 1080 « Exploiter le codage d'une figure pour
 * identifier des parallélogrammes particuliers ».
 *
 * Entrée PARTAGÉE par les deux formats de lecture (nature_qcm_unique,
 * nature_classer) : même geste — deux questions sur le codage, jamais sur
 * l'allure. L'exemple (revue 2026-09-02) est le piège central de la carte :
 * une figure DESSINÉE comme un carré, codée seulement par ses quatre angles
 * droits → un rectangle. SVG pas à pas piloté par les flèches de la sheet
 * (data-etapes / data-caption, mécanisme 2026-08-21).
 *
 * coder_qcm_unique : entrée dédiée (coder un losange, contre-exemple barré).
 */

const SVG_DEUX_QUESTIONS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Lire le codage d'une figure, en 3 étapes à faire défiler avec les flèches. Une figure PQRS, un peu penchée, porte un petit carré d'angle droit à chacun de ses quatre sommets et aucun trait sur ses côtés. Première question : un petit carré à chaque sommet ? Oui. Deuxième question : le même trait sur les quatre côtés ? Non, rien n'est codé, même si le dessin ressemble à un carré. D'après le codage, c'est un rectangle. Ce qui n'est pas codé ne se suppose jamais">
  <style>
    @keyframes cp1080-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1080-pop { opacity: 0; animation: cp1080-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Quatre petits carrés, aucun trait : quelle nature ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Angles droits : oui. Côtés égaux codés : non</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="3" font-family="system-ui,sans-serif">D'après le codage : un rectangle</text>

  <!-- ── La figure codée (étape 1) ───────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <path d="M 46.6 159.6 L 134.6 178.4 L 153.4 90.4 L 65.4 71.6 Z" fill="#eff6ff" stroke="#1E3A8A" stroke-width="2.2"/>
    <polyline points="56.4,161.7 58.5,151.9 48.7,149.8" fill="none" stroke="#111827" stroke-width="1.8"/>
    <polyline points="124.8,176.3 126.9,166.5 136.7,168.6" fill="none" stroke="#111827" stroke-width="1.8"/>
    <polyline points="151.3,100.2 141.5,98.1 143.6,88.3" fill="none" stroke="#111827" stroke-width="1.8"/>
    <polyline points="75.2,73.7 73.1,83.5 63.3,81.4" fill="none" stroke="#111827" stroke-width="1.8"/>
    <text x="36" y="172" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">P</text>
    <text x="146" y="192" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">Q</text>
    <text x="166" y="86" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">R</text>
    <text x="56" y="62" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">S</text>
  </g>

  <!-- ── Les deux questions (étape 2) ────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1080-pop">
      <polyline points="56.4,161.7 58.5,151.9 48.7,149.8" fill="none" stroke="#1D9E75" stroke-width="2.6"/>
      <polyline points="124.8,176.3 126.9,166.5 136.7,168.6" fill="none" stroke="#1D9E75" stroke-width="2.6"/>
      <polyline points="151.3,100.2 141.5,98.1 143.6,88.3" fill="none" stroke="#1D9E75" stroke-width="2.6"/>
      <polyline points="75.2,73.7 73.1,83.5 63.3,81.4" fill="none" stroke="#1D9E75" stroke-width="2.6"/>
      <path d="M 46.6 159.6 L 134.6 178.4 L 153.4 90.4 L 65.4 71.6 Z" fill="none" stroke="#B45309" stroke-width="2.2" stroke-dasharray="6 4"/>
      <text x="186" y="96" font-size="11.5" font-weight="700" fill="#1D9E75">Un petit carré à chaque</text>
      <text x="186" y="110" font-size="11.5" font-weight="700" fill="#1D9E75">sommet ? OUI ✓</text>
      <text x="186" y="134" font-size="11.5" font-weight="700" fill="#B45309">Le même trait sur les</text>
      <text x="186" y="148" font-size="11.5" font-weight="700" fill="#B45309">quatre côtés ? NON ✗</text>
      <text x="186" y="164" font-size="10.5" font-weight="600" fill="#B45309">(rien n'est codé, même si</text>
      <text x="186" y="177" font-size="10.5" font-weight="600" fill="#B45309">ça ressemble à un carré)</text>
    </g>
  </g>

  <!-- ── La conclusion (étape 3) ─────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1080-pop">
      <rect x="186" y="186" width="104" height="22" rx="8" fill="#ecfdf5" stroke="#1D9E75"/>
      <text x="238" y="201" font-size="12" font-weight="800" fill="#1D9E75" text-anchor="middle">un rectangle</text>
      <rect x="20" y="216" width="320" height="24" rx="10" fill="#eff6ff" stroke="#bfdbfe"/>
      <text x="180" y="232" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Ce qui n'est pas codé ne se suppose jamais.</text>
    </g>
  </g>

  <text x="180" y="248" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

/*
 * coder_qcm_unique : objectif « pouvoir affirmer : un losange ». Le bon
 * codage (le même trait sur les quatre côtés) puis le contre-exemple barré
 * (un trait sur un seul côté ne compare rien).
 */
const SVG_CODER_LOSANGE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Quels codages porter, en 3 étapes à faire défiler avec les flèches. Une figure PQRS penchée, sans aucun codage ; objectif : pouvoir affirmer que c'est un losange. Je porte le même trait sur les quatre côtés : quatre côtés égaux, c'est un losange. Contre-exemple : un trait sur le seul côté PQ ne compare rien, on ne peut rien affirmer. Je code tout ce que la nature exige">
  <style>
    @keyframes cp1080b-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1080b-pop { opacity: 0; animation: cp1080b-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Objectif : pouvoir affirmer « un losange »</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Le même trait sur les quatre côtés : losange ✓</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Un seul trait ne compare rien : rien à affirmer ✗</text>

  <!-- ── La figure nue et l'objectif (étape 1) ───────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <path d="M 100 180 L 160 125 L 100 70 L 40 125 Z" fill="#eff6ff" stroke="#1E3A8A" stroke-width="2.2"/>
    <text x="100" y="196" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">P</text>
    <text x="172" y="129" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">Q</text>
    <text x="100" y="62" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">R</text>
    <text x="28" y="129" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">S</text>
    <text x="190" y="52" font-size="11.5" font-weight="700" fill="#111827">Je veux pouvoir affirmer :</text>
    <text x="190" y="67" font-size="11.5" font-weight="700" fill="#111827">« un losange ».</text>
  </g>

  <!-- ── Le bon codage : le même trait partout (étape 2) ─────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1080b-pop">
      <line x1="125.9" y1="148.1" x2="134.1" y2="156.9" stroke="#1D9E75" stroke-width="2.6"/>
      <line x1="125.9" y1="101.9" x2="134.1" y2="93.1" stroke="#1D9E75" stroke-width="2.6"/>
      <line x1="74.1" y1="101.9" x2="65.9" y2="93.1" stroke="#1D9E75" stroke-width="2.6"/>
      <line x1="74.1" y1="148.1" x2="65.9" y2="156.9" stroke="#1D9E75" stroke-width="2.6"/>
      <text x="190" y="90" font-size="11.5" font-weight="700" fill="#1D9E75">Le même trait sur les quatre</text>
      <text x="190" y="104" font-size="11.5" font-weight="700" fill="#1D9E75">côtés : quatre côtés égaux ✓</text>
    </g>
  </g>

  <!-- ── Le contre-exemple barré (étape 3) ───────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1080b-pop">
      <path d="M 270 150 L 300 120 L 270 90 L 240 120 Z" fill="#fffbeb" stroke="#B45309" stroke-width="1.8"/>
      <line x1="281.5" y1="131.5" x2="288.5" y2="138.5" stroke="#B45309" stroke-width="2.2"/>
      <line x1="236" y1="154" x2="304" y2="86" stroke="#B45309" stroke-width="2.4"/>
      <text x="190" y="172" font-size="11.5" font-weight="700" fill="#B45309">Un trait sur [PQ] seulement :</text>
      <text x="190" y="186" font-size="11.5" font-weight="700" fill="#B45309">il ne compare rien ✗</text>
      <rect x="20" y="216" width="320" height="24" rx="10" fill="#eff6ff" stroke="#bfdbfe"/>
      <text x="180" y="232" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Je code tout ce que la nature exige.</text>
    </g>
  </g>

  <text x="180" y="248" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_LIRE = {
    pages: [
        {
            titre: 'Seuls les codages font foi',
            lignes: [
                '**Seuls les codages font foi** : ce qui est codé, je l\'affirme ; ce qui n\'est pas codé, je ne le suppose jamais, même si le dessin « a l\'air ».',
                '**Deux questions, toujours les mêmes** : un petit carré à chaque sommet ? le même trait sur les quatre côtés ?',
                'Les deux : carré. Les petits carrés seuls : rectangle. Les traits seuls : losange. Ni l\'un ni l\'autre : on ne peut rien affirmer de plus.',
            ],
        },
        { titre: 'Un exemple : la figure PQRS', svg: SVG_DEUX_QUESTIONS },
    ],
};

const CP_1080 = {
    nature_qcm_unique: AIDE_LIRE,
    nature_classer: AIDE_LIRE,
    coder_qcm_unique: {
        pages: [
            {
                titre: 'Coder tout ce que la nature exige',
                lignes: [
                    '**Pour pouvoir affirmer une nature, je code tout ce qu\'elle exige** : un losange, ses quatre côtés égaux (le même trait sur les quatre) ; un rectangle, ses quatre angles droits (un petit carré à chaque sommet) ; un carré, les deux.',
                    '**Un trait sur un seul côté ne dit rien** : il ne compare rien. Deux signes différents disent seulement « côtés opposés égaux », jamais « quatre côtés égaux ».',
                    '**Un codage de trop change la conclusion** : quatre petits carrés ajoutés à un losange en font un carré.',
                ],
            },
            { titre: 'Un exemple : coder un losange', svg: SVG_CODER_LOSANGE },
        ],
    },
};

export default CP_1080;
